import { useState } from "react";
import { type PlaceFeature, type RelatedPlace } from "~/lib/api";
import type { DetailLevel } from "~/lib/detailLevel";
import { cn } from "~/lib/utils";
import { PlaceStats } from "~/components/place/PlaceStats";
import { PlaceTypeLabel } from "~/components/place/PlaceTypeLabel";
import { Icon } from "~/components/ui/icons/icon";
import { ImageGallery } from "~/components/place/ImageGallery";
import { ImageLightbox } from "~/components/place/ImageLightbox";
import { PlaceDetailTabs } from "~/components/landing/PlaceDetailTabs";

interface PlaceDetailContentProps {
  place: PlaceFeature;
  details: DetailLevel;
  onBack: () => void;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
}

export function PlaceDetailContent({ place, details, onBack, onSelectRelatedPlace }: PlaceDetailContentProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { properties } = place;
  const { images, name, type, description, links, email, phone, pricing } = properties;
  const showGallery = images.length > 0 && details !== "minimal";

  return (
    <div className="flex flex-col">
      <ImageLightbox
        images={images}
        placeName={name}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {showGallery && (
        <div className="relative">
          <ImageGallery
            images={images}
            placeName={name}
            onClick={(index) => {
              setLightboxIndex(index);
              setLightboxOpen(true);
            }}
            className={cn({
              "h-44": details === "compact",
              "h-56": details === "full",
            })}
          />
          <button
            onClick={onBack}
            className="absolute top-3 left-3 size-9 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex items-center justify-center text-foreground hover:bg-white transition-colors"
            aria-label="Back to map overview"
          >
            <Icon i="back" className="size-5" />
          </button>
        </div>
      )}

      <div
        className={cn("px-5 pb-6", {
          "pt-5": showGallery,
          "pt-3": !showGallery && details !== "minimal",
          "pt-1": details === "minimal",
        })}
      >
        <div className={cn("flex items-start gap-4", { "flex-col": details === "full" })}>
          {!showGallery && (
            <button
              onClick={onBack}
              className="shrink-0 size-9 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              aria-label="Back to map overview"
            >
              <Icon i="back" className="size-5" />
            </button>
          )}

          <div className="flex flex-col min-w-0">
            <PlaceTypeLabel type={type} pricing={pricing} />
            <h2
              className={cn("text-2xl font-medium leading-tight", {
                "mt-1": details !== "minimal",
              })}
            >
              {name}
            </h2>
          </div>
        </div>

        <PlaceStats properties={properties} className="mt-2 text-muted-foreground" />

        <PlaceDetailTabs
          placeId={place.id as string}
          type={type}
          description={description}
          links={links}
          email={email}
          phone={phone}
          pricing={pricing}
          relatedPlaces={properties.relatedPlaces}
          onSelectRelatedPlace={onSelectRelatedPlace}
          className="mt-3"
        />
      </div>
    </div>
  );
}
